import { getI18nInstance as vueI18n } from '@/i18n';
import { commitToast } from '@/plugins/toast';
import dayjs from 'dayjs';
import { globalParam } from './globals';

/**
 * Tries to make sense of the given "Error", which may be a string,
 * an error object, some kind of response.... you name it.
 * @param {Something} error The error "thingie"
 * @return A string representing the error msg.
 */
export function errToStr(error: any): string {
    let msg = 'Unbekannter Fehler';

    if (error) {
        // Try to extract a data property:
        const data = error?.response?.data || error?.response?.response?.data;

        if (data?.error?.msg) {
            msg = data.error.msg;
        } else if (error?.response?.responseText) {
            const obj = JSON.parse(error.response.responseText);
            if (obj?.error?.msg) {
                msg = `${obj.error.code}: ${obj.error.msg}`;
            } else {
                msg = String(error.response.responseText);
            }
        } else if (error?.responseText) {
            const obj = JSON.parse(error.responseText);
            if (obj?.error?.msg) {
                msg = `${obj.error.code}: ${obj.error.msg}`;
            } else {
                msg = String(error.responseText);
            }
        } else if (typeof error === 'string') {
            const obj = JSON.parse(error);
            if (obj?.error?.msg) {
                msg = `${obj.error.code}: ${obj.error.msg}`;
            } else {
                msg = String(error);
            }
        } else {
            msg = String(error);
        }
    }
    return msg;
}

/**
 * Common form rules, to be used as rules in vuetify forms
 */
export const formRules = {
    /** Value is required: */
    required: (value: number | boolean | string | Array<any> | object) => {
        if (value instanceof Array) {
            return value.length > 0 || vueI18n().t('value_please');
        } else if (typeof value === 'string') {
            return !!value.trim() || vueI18n().t('value_please');
        } else {
            return !!value || vueI18n().t('value_please');
        }
    },

    /** Must be an email address: */
    email: (value: string) => {
        const pattern =
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return value ? pattern.test(value) || vueI18n().t('invalid_email') : true;
    },

    url: (value: string) => {
        // too complex to test all possible cases
        // @see https://mathiasbynens.be/demo/url-regex
        // a updated version of the regex with most matches
        // @see https://gist.github.com/dperini/729294
        // we decided to not use url validation for now and we will see if it really was a good decision (18.03.2025) #29698
        const pattern =
            /^(?:(?:(?:https?|ftp):)?\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z0-9\u00a1-\uffff][a-z0-9\u00a1-\uffff_-]{0,62})?[a-z0-9\u00a1-\uffff]\.)+(?:[a-z\u00a1-\uffff]{2,}\.?))(?::\d{2,5})?(?:[/?#]\S*)?$/i;
        return value ? pattern.test(value) || vueI18n().t('invalid_url') : true;
    },

    /** Only alpha-chars */
    charsOnly: (value: string) => {
        const antiPattern = /[0-9]/;
        return value ? !antiPattern.test(value) || vueI18n().t('chars_only') : true;
    },

    /**
     * Creator function: use with formRules.minLength(8) to get a min-8-length validator function:
     */
    minLength: (minLength: number, ignoreWhitespace: boolean) => (value: string) => {
        if (value && ignoreWhitespace) {
            value = value.replaceAll(' ', '');
        }
        return (value || '').length >= minLength || vueI18n().t('min_chars', { chars: minLength });
    },
    /**
     * Creator function: use with formRules.minLengthOptional(8) to allow empty value or at least 8 characters
     */
    minLengthOptional: (minLength: number, ignoreWhitespace: boolean) => (value: string) => {
        if (!value) {
            return true;
        }
        if (value && ignoreWhitespace) {
            value = value.replaceAll(' ', '');
        }
        return (value || '').length >= minLength || vueI18n().t('min_chars', { chars: minLength });
    },
    /**
     * Creator function: use with formRules.maxLength(10) to get a max-10-length validator function:
     */
    maxLength: (maxLength: number, ignoreWhitespace: boolean) => (value: string) => {
        if (value && ignoreWhitespace) {
            value = value.replaceAll(' ', '');
        }
        return (value || '').length <= maxLength || vueI18n().t('max_chars', { chars: maxLength });
    },

    /**
     * Creator function: use with formRules.between(1000,2000) to allow numbers between 1000 and 2000
     */
    between: (min: number, max: number, errorMsg: string) => (value: number | '' | null) => {
        if (value === null || value === '') {
            return true;
        }
        if (min === null && max === null) {
            return true;
        }
        if (min === null && max !== null) {
            return value <= max ? true : errorMsg || vueI18n().t('value_max', { max });
        }
        if (max === null && min !== null) {
            return value >= min ? true : errorMsg || vueI18n().t('value_min', { min });
        }
        return value >= min && value <= max ? true : errorMsg || vueI18n().t('value_between', { min, max });
    },

    /**
     *  UID Number in the format like CHE-106.050.103
     */
    uid: (value: string) => {
        const pattern = /^CHE-[0-9][0-9][0-9]\.[0-9][0-9][0-9]\.[0-9][0-9][0-9]$/;
        return value ? pattern.test(value) || vueI18n().t('invalid_uid') : true;
    },

    dateFormat: (dateStr: string) => (value: string) => {
        return !value || dayjs(value, dateStr, true).isValid() || vueI18n().t('wrong_format', { format: dateStr });
    },

    isInt: () => (value: string) => value ? /^\d*$/.test(value) || vueI18n().t('int_only') : true,

    houseNumber: (value: string) => {
        const pattern = /^[0-9a-zA-Z]*\.?[0-9a-zA-Z]*$/;
        return value ? pattern.test(value) || vueI18n().t('invalid_house_number') : true;
    },

    iban: (value: string) => {
        if (!value) {
            return false;
        }
        if (value.startsWith('CH') || value.startsWith('LI')) {
            value = value.replaceAll(' ', '');
            return (value || '').length === 21 || `${value.length}/${21}`;
        }
        if (value.startsWith('AT')) {
            value = value.replaceAll(' ', '');
            return (value || '').length === 20 || `${value.length}/${20}`;
        }
        if (value.startsWith('DE')) {
            value = value.replaceAll(' ', '');
            return (value || '').length === 22 || `${value.length}/${22}`;
        }
        return true;
    },

    /**
     * Special rule for event.summary_text: This is a combination of multiple rules,
     * and can be customer-specific. See comment below.
     * @param value The summary text value of the form field
     * @returns true|string
     */
    eventSummaryText: (value: string): true | string => {
        const ruleRet = formRules.maxLength(globalParam('event.max_summary_text_chars', 200))(value);
        if (typeof ruleRet === 'string') {
            return ruleRet;
        }
        // summary text is only mandatory for some customers. set in env.php:
        if (fieldRuleValue('field_rules.mandatory.event.summary_text', false as boolean)) {
            return formRules.required(value);
        }

        return true;
    },

    eventAbstractText: (value: string): true | string => {
        const ruleRet = formRules.maxLength(globalParam('event.max_abstract_text_chars', 60))(value);
        if (typeof ruleRet === 'string') {
            return ruleRet;
        }

        return true;
    }
} as { [key: string]: any };

/**
 * Takes a data table 'options' object, as delvered by the Vuetify data table.
 * It contains  paging and sorting infos, as in this example:
 *
 * <code>
 *  tableOptions: {
        page: 1,
        itemsPerPage: 50,
        sortBy: ['username'],
        sortDesc: [false],
        multiSort: false,
        mustSort: true
    }
 * </code>
 *
 * For sending it to the backend, we need to convert it to a proper API format,
 * to be used as POST params with axios.
 */
export function tableOptionsToQueryParams(tableOptions: {
    itemsPerPage?: number;
    page?: number;
    sortBy?: string[];
    sortDesc?: boolean[];
}) {
    const opts = {
        limit: tableOptions.itemsPerPage,
        start: ((tableOptions.page || 1) - 1) * (tableOptions.itemsPerPage || 0), // vuetify delivers the page number, we need a record offset
        sort: '' as string,
    };
    const sort = [] as Array<{ property: string; direction: string }>;
    if (tableOptions.sortBy && tableOptions.sortBy.length > 0) {
        for (let i = 0; i < tableOptions.sortBy.length; i++) {
            const sortBy = tableOptions.sortBy[i].replace(/^\$\./, ''); // replace '$.foo' with 'foo': $. is from vue-mc's "active value".
            const sortDesc =
                tableOptions.sortDesc && tableOptions.sortDesc.length - 1 >= i ? tableOptions.sortDesc[i] : null;
            sort.push({ property: sortBy, direction: sortDesc ? 'desc' : 'asc' });
        }
    }
    opts.sort = JSON.stringify(sort);

    return opts;
}

/**
 * Creates a function that can only be called "debounced", means, only all n milliseconds.
 *
 * @param      {Function}  fn      The function to execute
 * @param      {Number}    millis  The nr of milliseconds in which the function is NOT called again
 * @return     {Function}  a new Function that can rapidly be called, but is only invoked max all n milliseconds.
 */
export function debounce(fn: (...args: any) => void, millis = 350) {
    // hoisted timeout variable
    let timeout: number | null = null;
    const callbackFn = () => (timeout = null);

    return function (...args: any) {
        if (timeout) {
            return;
        }
        timeout = setTimeout(callbackFn, millis);
        // @ts-ignore: "this" is not known here: it is taken from the calling context (which is not a good thing, though....)
        fn.apply(this, args);
    };
}

/**
 * Creates a function that is only called after n miliseconds. If the function is called again within this time,
 * then the timer is reset and the delay is applied again.
 *
 * Useful for delaying search input execution (e.g. if the user is typing...)
 *
 * @param      {Function}  fn      The function to execute
 * @param      {Number}    millis  The nr of milliseconds to wait until execution
 * @return     {Function}  a new Function that can rapidly be called, but is only invoked after the last call is n milis away.
 */
export function delayed(fn: (...args: any) => void, millis = 350) {
    // hoisted timeout variable
    let timeout: number | null = null;

    return function (...args: any) {
        if (timeout) {
            clearTimeout(timeout);
            timeout = null;
        }
        // @ts-ignore: "this" is not known here: it is taken from the calling context (which is not a good thing, though....)
        timeout = setTimeout(() => fn.apply(this, args), millis);
    };
}

export function setBrowserTitle(title: string) {
    document.title = (title || '') + (title ? ' - Minasa CMS' : 'Minasa CMS');
}

/**
 * Converts a possible number to an ID value: IDs are numbers, OR null, if the value is 0.
 */
export function convertToId(input: string | number | null | undefined): number | null {
    return Number(input) || null;
}

export async function copyTextToClipboard(text: string) {
    try {
        await window.navigator?.clipboard?.writeText(text);
        commitToast({ message: vueI18n().t('copied') as string, color: 'success', timeout: 1000 });
    } catch (e) {
        // pass
    }
}

export function shortenString(string: string, length: number) {
    if (!string || !length || string.length < length) {
        return string;
    }
    return string.substring(0, length - 1) + '…';
}

export function nl2br(str: string): string {
    if (typeof str === 'string') {
        return str.replace(/\r/g, '').replace(/\n/g, '<br/>');
    } else return '';
}

export function stripHtml(str: string | null | undefined): string {
    str = str ?? '';
    const div = document.createElement('div');
    div.innerHTML = str;
    return div.textContent ?? div.innerText ?? '';
}

/**
 * Returns a value from the GLOBALS.params.field_rules object, or
 * a default value if the param does not exist.
 * @param ident
 * @param defaultValue
 * @returns
 */
export function fieldRuleValue<T>(ident: string, defaultValue: T): T {
    const rules = globalParam('field_rules', {}) as { [key: string]: any };

    if (Object.prototype.hasOwnProperty.call(rules, ident)) {
        return rules[ident];
    }
    return defaultValue;
}
